import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Reveal } from "@/lib/reveal";
import { submitBooking, submitNetlifyForm } from "@/lib/api";
import { SERVICES } from "@/data/services";
import { Calendar, Clock, Check, AlertCircle, Mail, MessageCircle, HeartHandshake, Coffee, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import CalendlyInline from "@/components/CalendlyInline";
import { usePageMeta } from "@/lib/usePageMeta";

const EXPECT_STEPS = [
  {
    icon: Mail,
    title: "You reach out",
    body: "A few words is enough. No long forms, no pressure — just a hello.",
  },
  {
    icon: MessageCircle,
    title: "We pick a time, together",
    body: "I'll reply within a day with a few options that fit your week.",
  },
  {
    icon: Coffee,
    title: "We meet — your way",
    body: "On the phone, on a walk, over coffee, or simply in quiet conversation.",
  },
  {
    icon: HeartHandshake,
    title: "No follow-up pressure",
    body: "Come back whenever you'd like. Or don't. The door stays open either way.",
  },
];

export default function BookingPage() {
  usePageMeta({
    title: "Book a Session — Calm & Connected",
    description:
      "Schedule a one-on-one peer support conversation. Pick a time on the Calendly calendar, or send a quick note and we'll find a time together.",
  });
  // Which session length the visitor is booking. Defaults to the featured
  // 60-minute Standard tier.
  const defaultSession =
    SERVICES.find((s) => s.featured) || SERVICES[0];
  const [selectedSessionId, setSelectedSessionId] = useState(defaultSession.id);
  const selectedSession =
    SERVICES.find((s) => s.id === selectedSessionId) || defaultSession;

  // Support deep-linking from the Pricing page: /booking?session=mini|standard|extended
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const q = searchParams.get("session");
    if (q && SERVICES.some((s) => s.id === q)) {
      setSelectedSessionId(q);
    }
  }, [searchParams]);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    preferred_times: "",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", error: "" });

    const payload = { ...form };
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "") delete payload[k];
    });

    // Try Netlify Forms first (primary on production). Fall back to the
    // FastAPI backend if Netlify is unreachable (e.g. local dev).
    let netlifyOk = false;
    try {
      await submitNetlifyForm("booking", { ...payload, "bot-field": "" });
      netlifyOk = true;
    } catch (_) {
      // fall through to backend
    }

    try {
      if (!netlifyOk) {
        await submitBooking(payload);
      }
      setStatus({ state: "success", error: "" });
      setForm({ name: "", email: "", phone: "", service: "", preferred_times: "", message: "" });
    } catch (err) {
      setStatus({
        state: "error",
        error:
          err?.response?.data?.detail?.[0]?.msg ||
          err?.message ||
          "Something went wrong. Please try again.",
      });
    }
  };

  return (
    <div data-testid="booking-page">
      {/* Header */}
      <section className="pt-36 md:pt-44 pb-12">
        <div className="cc-container max-w-3xl">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
              Booking
            </p>
            <h1 className="mt-5 font-serif text-5xl md:text-6xl leading-[1.05]">
              Schedule a Conversation
            </h1>
            <p className="mt-7 text-lg text-[var(--cc-text-soft)] leading-relaxed">
              Pick a time that feels right using the calendar below — or, if
              you&apos;d rather start with a few words, scroll down and send
              me a note instead.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Calendly inline scheduler */}
      <section className="pb-12" data-testid="booking-calendly-section">
        <div className="cc-container">
          <Reveal>
            <div className="cc-card p-4 md:p-6 bg-white">
              <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-5 mb-5 px-2 md:px-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[var(--cc-surface)] flex items-center justify-center shrink-0 border border-[var(--cc-line)]">
                    <Calendar size={16} strokeWidth={1.5} className="text-[var(--cc-primary)]" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl md:text-2xl leading-tight">
                      Book a time
                    </h3>
                    <p className="text-sm text-[var(--cc-text-soft)]">
                      Choose a session length, then pick any open slot.
                    </p>
                  </div>
                </div>
              </div>

              {/* Session-length tabs */}
              <div
                role="tablist"
                aria-label="Choose a session length"
                data-testid="booking-session-tabs"
                className="mx-1 md:mx-2 mb-4 md:mb-5 grid grid-cols-1 sm:grid-cols-3 gap-2 p-1.5 rounded-2xl bg-[var(--cc-surface)] border border-[var(--cc-line)]"
              >
                {SERVICES.map((s) => {
                  const active = s.id === selectedSessionId;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      data-testid={`booking-session-tab-${s.id}`}
                      onClick={() => setSelectedSessionId(s.id)}
                      className={`relative text-left sm:text-center px-4 py-3 rounded-xl transition-all duration-300 border ${
                        active
                          ? "bg-white border-[var(--cc-primary)]/40 shadow-[0_6px_18px_rgba(0,0,0,0.05)]"
                          : "bg-transparent border-transparent hover:bg-white/60"
                      }`}
                    >
                      <span className="block text-[10px] uppercase tracking-[0.2em] text-[var(--cc-text-soft)]">
                        {s.duration}
                      </span>
                      <span
                        className={`mt-1 block font-serif text-lg leading-tight ${
                          active
                            ? "text-[var(--cc-text)]"
                            : "text-[var(--cc-text)]/85"
                        }`}
                      >
                        {s.name}
                      </span>
                      <span className="mt-1 block text-sm text-[var(--cc-text-soft)]">
                        {s.price}
                        {s.featured && (
                          <span className="ml-2 inline-block align-middle px-2 py-0.5 rounded-full bg-[var(--cc-primary)] text-white text-[9px] uppercase tracking-[0.16em]">
                            Most chosen
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              <CalendlyInline
                key={selectedSession.calendly}
                url={selectedSession.calendly}
                height={760}
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* What to expect timeline */}
      <section
        data-testid="booking-what-to-expect"
        className="pb-16 md:pb-24 relative overflow-hidden"
      >
        <span aria-hidden className="cc-orb cc-orb-2" style={{ opacity: 0.22 }} />
        <div className="cc-container relative">
          <div className="max-w-2xl">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
                What to expect
              </p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
                A gentle four-step <em className="italic">hello.</em>
              </h2>
              <p className="mt-5 text-[var(--cc-text-soft)] leading-relaxed">
                Reaching out should feel as easy as a deep breath. Here&apos;s
                what happens after you send a note — nothing more, nothing pushy.
              </p>
            </Reveal>
          </div>

          <ol className="mt-14 grid md:grid-cols-4 gap-6 md:gap-5 relative">
            <div
              aria-hidden
              className="hidden md:block absolute top-7 left-[8%] right-[8%] h-px bg-[var(--cc-primary)]/25"
              style={{
                backgroundImage:
                  "linear-gradient(to right, var(--cc-primary) 0 6px, transparent 6px 14px)",
                backgroundSize: "14px 1px",
              }}
            />
            {EXPECT_STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={i * 140}>
                  <li
                    data-testid={`expect-step-${i}`}
                    className="relative flex flex-col items-start md:items-center md:text-center"
                  >
                    <span className="relative z-10 w-14 h-14 rounded-full bg-white border border-[var(--cc-line)] shadow-[0_8px_20px_rgba(0,0,0,0.04)] flex items-center justify-center">
                      <Icon size={20} strokeWidth={1.5} className="text-[var(--cc-primary)]" />
                    </span>
                    <span className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[var(--cc-text-soft)]">
                      Step {i + 1}
                    </span>
                    <h3 className="mt-2 font-serif text-xl md:text-2xl leading-snug">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm md:text-base text-[var(--cc-text-soft)] leading-relaxed">
                      {s.body}
                    </p>
                  </li>
                </Reveal>
              );
            })}
          </ol>
        </div>
      </section>

      {/* Form */}
      <section className="pb-24 md:pb-32">
        <div className="cc-container">
          <Reveal>
            <div className="max-w-2xl mb-10 md:mb-12">
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
                Prefer words first?
              </p>
              <h2 className="mt-4 font-serif text-3xl md:text-4xl leading-tight">
                Send a note instead.
              </h2>
              <p className="mt-4 text-[var(--cc-text-soft)] leading-relaxed">
                If choosing a time feels like a lot right now, share a few
                words below and I&apos;ll write back to find something that
                fits.
              </p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-5">
            <div className="cc-card p-8 md:p-10 h-full">
              <h3 className="font-serif text-3xl">A few practical notes</h3>
              <ul className="mt-6 space-y-4 text-[var(--cc-text-soft)]">
                <li className="flex items-start gap-3">
                  <Clock size={16} strokeWidth={1.5} className="mt-1 text-[var(--cc-primary)] shrink-0" />
                  I usually reply within a day, often the same day.
                </li>
                <li className="flex items-start gap-3">
                  <Check size={16} strokeWidth={1.7} className="mt-1 text-[var(--cc-primary)] shrink-0" />
                  You can share as much or as little as feels right.
                </li>
                <li className="flex items-start gap-3">
                  <Check size={16} strokeWidth={1.7} className="mt-1 text-[var(--cc-primary)] shrink-0" />
                  We'll find a time and a session length that fits your week.
                </li>
              </ul>

              <div className="mt-10 pt-8 border-t border-[var(--cc-line)]">
                <p className="text-xs uppercase tracking-[0.18em] text-[var(--cc-text-soft)]">
                  Quick contact
                </p>
                <p className="mt-3 text-[var(--cc-text)]">
                  <a className="cc-link" href="mailto:calmconnects@outlook.com">
                    calmconnects@outlook.com
                  </a>
                </p>
                <p className="mt-1 text-[var(--cc-text)]">
                  <a className="cc-link" href="tel:+15022494937">
                    (502) 249-4937
                  </a>
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120} className="md:col-span-7">
            <form
              data-testid="booking-form"
              name="booking"
              method="POST"
              data-netlify="true"
              onSubmit={onSubmit}
              className="cc-card p-8 md:p-10"
            >
              <input type="hidden" name="form-name" value="booking" />
              <p className="hidden" aria-hidden="true">
                <label>
                  Don&apos;t fill this out if you&apos;re human:{" "}
                  <input name="bot-field" tabIndex="-1" autoComplete="off" />
                </label>
              </p>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="b-name" className="block text-sm mb-2 text-[var(--cc-text-soft)]">
                    Name
                  </label>
                  <input
                    id="b-name"
                    name="name"
                    type="text"
                    required
                    data-testid="booking-input-name"
                    className="cc-input"
                    placeholder="What should I call you?"
                    value={form.name}
                    onChange={update("name")}
                  />
                </div>
                <div>
                  <label htmlFor="b-email" className="block text-sm mb-2 text-[var(--cc-text-soft)]">
                    Email
                  </label>
                  <input
                    id="b-email"
                    name="email"
                    type="email"
                    required
                    data-testid="booking-input-email"
                    className="cc-input"
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={update("email")}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5 mt-5">
                <div>
                  <label htmlFor="b-phone" className="block text-sm mb-2 text-[var(--cc-text-soft)]">
                    Phone <span className="text-[var(--cc-text-soft)]/60">(optional)</span>
                  </label>
                  <input
                    id="b-phone"
                    name="phone"
                    type="tel"
                    data-testid="booking-input-phone"
                    className="cc-input"
                    placeholder="(optional)"
                    value={form.phone}
                    onChange={update("phone")}
                  />
                </div>
                <div>
                  <label htmlFor="b-service" className="block text-sm mb-2 text-[var(--cc-text-soft)]">
                    Session length
                  </label>
                  <select
                    id="b-service"
                    name="service"
                    data-testid="booking-input-service"
                    className="cc-input"
                    value={form.service}
                    onChange={update("service")}
                  >
                    <option value="">Not sure yet</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.name}>
                        {s.name} — {s.duration} ({s.price})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="b-times" className="block text-sm mb-2 text-[var(--cc-text-soft)]">
                  Preferred days &amp; times
                </label>
                <input
                  id="b-times"
                  name="preferred_times"
                  type="text"
                  data-testid="booking-input-times"
                  className="cc-input"
                  placeholder="e.g. Weekday evenings, Saturday afternoons…"
                  value={form.preferred_times}
                  onChange={update("preferred_times")}
                />
              </div>

              <div className="mt-5">
                <label htmlFor="b-message" className="block text-sm mb-2 text-[var(--cc-text-soft)]">
                  Anything you'd like me to know
                </label>
                <textarea
                  id="b-message"
                  name="message"
                  data-testid="booking-input-message"
                  className="cc-input"
                  placeholder="Even a few words are okay."
                  value={form.message}
                  onChange={update("message")}
                />
              </div>

              {status.state === "error" && (
                <div
                  data-testid="booking-form-error"
                  className="mt-5 flex items-start gap-2.5 p-4 rounded-2xl bg-[#FBEEEE] border border-[#E8CFCF] text-[#7A3A3A] text-sm"
                >
                  <AlertCircle size={16} strokeWidth={1.7} className="mt-0.5 shrink-0" />
                  {status.error}
                </div>
              )}

              {status.state === "success" && (
                <div
                  data-testid="booking-form-success"
                  className="mt-5 flex items-start gap-2.5 p-4 rounded-2xl bg-[#EEF3EF] border border-[#CFE0D2] text-[#3A5A45] text-sm"
                >
                  <Check size={16} strokeWidth={1.8} className="mt-0.5 shrink-0" />
                  Thank you — your request was sent. I'll be in touch soon.
                </div>
              )}

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  data-testid="booking-submit"
                  disabled={status.state === "loading"}
                  className="cc-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status.state === "loading" ? "Sending…" : "Send when you're ready"}
                </button>
                <Link to="/contact" data-testid="booking-to-contact" className="cc-btn-secondary">
                  Just send a note
                </Link>
              </div>

              <div
                data-testid="booking-privacy-note"
                className="mt-6 flex items-start gap-2.5 text-xs text-[var(--cc-text-soft)]/90 leading-relaxed"
              >
                <Lock size={13} strokeWidth={1.6} className="mt-0.5 text-[var(--cc-primary)] shrink-0" />
                Your note comes only to Maria. Always. No newsletters, no third parties.
              </div>

              <p className="mt-3 text-xs text-[var(--cc-text-soft)]/80">
                This is not therapy or crisis support. If you are in crisis, please call 988.
              </p>
            </form>
          </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
